import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../LanguageContext';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  noindex?: boolean;
  schema?: any;
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  noindex = false,
  schema
}) => {
  const { language } = useLanguage();
  
  // Default values for Bulgarian market
  const defaults = {
    title: language === 'bg' 
      ? 'BoxFlow - Професионални Фулфилмънт Услуги | София, България'
      : 'BoxFlow - Professional Fulfillment Services | Sofia, Bulgaria',
    description: language === 'bg'
      ? 'BoxFlow предлага комплексни логистични услуги - складиране, опаковане и доставка с Еконт, Спиди, DHL. Фулфилмънт решения за вашия онлайн бизнес в България.'
      : 'BoxFlow offers comprehensive logistics services - storage, packaging and delivery with Econt, Speedy, DHL. Fulfillment solutions for your online business in Bulgaria.',
    keywords: language === 'bg'
      ? 'фулфилмънт, складиране, логистика, доставка, опаковане, еконт, спиди, DHL, онлайн магазин, електронна търговия, България, София, fulfillment България, логистични услуги, куриерски услуги, управление на поръчки, връщания, dropshipping България'
      : 'fulfillment, storage, logistics, delivery, packaging, econt, speedy, DHL, online store, e-commerce, Bulgaria, Sofia, fulfillment Bulgaria, logistics services, courier services, order management, returns, dropshipping Bulgaria',
    image: 'https://boxflow.bg/og-image.jpg',
    url: 'https://boxflow.bg'
  };

  const seoTitle = title || defaults.title;
  const seoDescription = description || defaults.description;
  const seoKeywords = keywords || defaults.keywords;
  const seoImage = image || defaults.image;
  const seoUrl = url || defaults.url;

  // Structured data for local business (Bulgarian market)
  const defaultSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://boxflow.bg",
    "name": "BoxFlow",
    "description": language === 'bg' 
      ? "Професионални фулфилмънт услуги за вашия онлайн бизнес"
      : "Professional fulfillment services for your online business",
    "url": "https://boxflow.bg",
    "telephone": "+359878686659",
    "email": "info@boxflow.bg",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "ул. Индустриална зона",
      "addressLocality": "София",
      "addressRegion": "София",
      "postalCode": "1000",
      "addressCountry": "BG"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 42.6977,
      "longitude": 23.3219
    },
    "image": [
      "https://boxflow.bg/logo.png",
      "https://boxflow.bg/warehouse.jpg"
    ],
    "sameAs": [
      "https://www.facebook.com/boxflow.bg",
      "https://www.linkedin.com/company/boxflow-bg",
      "https://www.instagram.com/boxflow.bg"
    ],
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      "opens": "09:00",
      "closes": "18:00"
    },
    "priceRange": "€€",
    "currenciesAccepted": "EUR, BGN",
    "paymentAccepted": "Cash, Credit Card, Bank Transfer",
    "areaServed": {
      "@type": "Country",
      "name": "Bulgaria"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": language === 'bg' ? "Фулфилмънт услуги" : "Fulfillment Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": language === 'bg' ? "Складиране" : "Storage",
            "description": language === 'bg' 
              ? "Сигурно съхранение на вашите продукти"
              : "Secure storage of your products"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": language === 'bg' ? "Опаковане и доставка" : "Packaging and Shipping",
            "description": language === 'bg'
              ? "Професионално опаковане и доставка с водещи куриери"
              : "Professional packaging and delivery with leading couriers"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": language === 'bg' ? "Интеграции" : "Integrations",
            "description": language === 'bg'
              ? "Интеграция с Shopify, WooCommerce и други платформи"
              : "Integration with Shopify, WooCommerce and other platforms"
          }
        }
      ]
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "47",
      "bestRating": "5",
      "worstRating": "1"
    }
  };

  const structuredData = schema || defaultSchema;

  // Generate alternate language URLs
  const alternateUrl = seoUrl.includes('?') 
    ? `${seoUrl}&lang=${language === 'bg' ? 'en' : 'bg'}`
    : `${seoUrl}?lang=${language === 'bg' ? 'en' : 'bg'}`;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{seoTitle}</title>
      <meta name="title" content={seoTitle} />
      <meta name="description" content={seoDescription} />
      <meta name="keywords" content={seoKeywords} />
      <meta name="author" content="BoxFlow" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta httpEquiv="content-language" content={language} />
      <meta name="language" content={language} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={seoUrl} />
      
      {/* Language alternates for SEO */}
      <link rel="alternate" hrefLang={language} href={seoUrl} />
      <link rel="alternate" hrefLang={language === 'bg' ? 'en' : 'bg'} href={alternateUrl} />
      <link rel="alternate" hrefLang="x-default" href="https://boxflow.bg" />
      
      {/* Robots Meta Tags */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <>
          <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
          <meta name="googlebot" content="index, follow" />
        </>
      )}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={seoUrl} />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:image" content={seoImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content={language === 'bg' ? 'bg_BG' : 'en_US'} />
      <meta property="og:locale:alternate" content={language === 'bg' ? 'en_US' : 'bg_BG'} />
      <meta property="og:site_name" content="BoxFlow" />
      
      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={seoUrl} />
      <meta property="twitter:title" content={seoTitle} />
      <meta property="twitter:description" content={seoDescription} />
      <meta property="twitter:image" content={seoImage} />
      <meta property="twitter:creator" content="@boxflow_bg" />
      
      {/* Additional meta tags for Bulgarian market */}
      <meta name="geo.region" content="BG-22" />
      <meta name="geo.placename" content="София" />
      <meta name="geo.position" content="42.6977;23.3219" />
      <meta name="ICBM" content="42.6977, 23.3219" />
      
      {/* Mobile optimization */}
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="format-detection" content="telephone=no" />
      
      {/* Performance hints */}
      <link rel="dns-prefetch" href="//fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      
      {/* Additional structured data for Bulgarian business */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": language === 'bg' ? "Начало" : "Home",
              "item": "https://boxflow.bg"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": language === 'bg' ? "Услуги" : "Services",
              "item": "https://boxflow.bg#services"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": language === 'bg' ? "Цени" : "Pricing",
              "item": "https://boxflow.bg#pricing"
            }
          ]
        })}
      </script>
      
      {/* FAQ Schema for common questions */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": language === 'bg' 
                ? "Какви куриерски услуги използва BoxFlow?"
                : "Which courier services does BoxFlow use?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": language === 'bg'
                  ? "BoxFlow работи с водещите куриерски компании в България - Еконт, Спиди и DHL, осигурявайки надеждна доставка до вашите клиенти."
                  : "BoxFlow works with leading courier companies in Bulgaria - Econt, Speedy and DHL, ensuring reliable delivery to your customers."
              }
            },
            {
              "@type": "Question",
              "name": language === 'bg'
                ? "Колко бързо се обработват поръчките?"
                : "How quickly are orders processed?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": language === 'bg'
                  ? "Обработваме и изпращаме поръчките в рамките на същия работен ден, ако са получени до 14:00 часа."
                  : "We process and ship orders within the same business day if received before 2:00 PM."
              }
            },
            {
              "@type": "Question",
              "name": language === 'bg'
                ? "Предлагате ли интеграция с онлайн магазини?"
                : "Do you offer integration with online stores?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": language === 'bg'
                  ? "Да, предлагаме интеграция с популярни платформи като Shopify, WooCommerce и други системи за електронна търговия."
                  : "Yes, we offer integration with popular platforms like Shopify, WooCommerce and other e-commerce systems."
              }
            }
          ]
        })}
      </script>
    </Helmet>
  );
};

export default SEO;
